import React, { useRef } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import type { FilterType, PageName, Settings } from '../shared/types';
import KeywordInput from './keyword_input';
import AiKeywordsForm from './ai_keywords_form';
import Api from '../lib/api';

interface Props {
  filterType: FilterType;
  isLoading?: boolean;
  onAdd: (keywords: string[], filterType: FilterType) => void;
  onRemove: (keyword: string, filterType: FilterType) => void;
  aiKeywords?: Array<string>;
  setAiKeywords?: (keywords: string[]) => void;
  values: Array<string>;
  regenerateKeywords?: () => void;
  error?: string | null;
  pageName: PageName;
  booleanIndicator?: string;
  settings?: Settings;
  apiClient?: Api;
}

const KeywordForm = ({
  filterType,
  isLoading = false,
  onAdd,
  onRemove,
  aiKeywords = [],
  setAiKeywords,
  values,
  regenerateKeywords,
  error = '',
  pageName,
  booleanIndicator,
  settings,
  apiClient,
}: Props) => {
  const popperRef = useRef<HTMLElement | null>(null);

  const onAddItem = (keywords: string[]) => {
    onAdd(keywords, filterType);
  };

  const lowerCaseValues = values.map((value) => value.toLowerCase());

  const displayedKeywords = aiKeywords.filter(
    (keyword) => !lowerCaseValues.includes(keyword.toLowerCase())
  );

  const onAddAllKeywords = () => {
    onAdd([...values, ...displayedKeywords], filterType);
  };

  const onRemoveAllKeywords = () => {
    const newValues = values.filter((value) => !aiKeywords.includes(value));

    onAdd(newValues, filterType);
  };

  return (
    <Stack data-provides={`${filterType}-input-section`} spacing={0}>
      <KeywordInput
        onAdd={onAddItem}
        onRemove={(keyword) => onRemove(keyword, filterType)}
        values={values}
        booleanIndicator={booleanIndicator}
        inputRef={popperRef}
      />
      {settings?.aiFeatureEnabled && (
        <AiKeywordsForm
          isLoading={isLoading}
          error={error}
          displayedKeywords={displayedKeywords}
          hasKeywords={aiKeywords.length > 0}
          onAddItem={(keyword) => onAddItem([...values, keyword])}
          onAddAll={onAddAllKeywords}
          onRemoveAll={onRemoveAllKeywords}
          regenerateKeywords={regenerateKeywords}
          showRegenerateButton={settings?.showRegenerateButton}
          pageName={pageName}
          popperRef={popperRef}
          apiClient={apiClient}
          setAiKeywords={setAiKeywords}
        />
      )}
    </Stack>
  );
};

export default KeywordForm;
